import gql from 'graphql-tag';

export interface AmazonDateFilters {
  createdAtFrom?: string;
  createdAtTo?: string;
  updatedAtFrom?: string;
  updatedAtTo?: string;
  expectedArrivalAtFrom?: string;
  expectedArrivalAtTo?: string;
  driverDropOffAtFrom?: string;
  driverDropOffAtTo?: string;
  consigneePickupAtFrom?: string;
  consigneePickupAtTo?: string;
  parcelAgedAtFrom?: string;
  parcelAgedAtTo?: string;
  dispossessedAtFrom?: string;
  dispossessedAtTo?: string;
  hasDriverDropOff?: boolean;
  hasConsigneePickup?: boolean;
  hasParcelAged?: boolean;
}

export type AmazonParcel = {
  amazonAccessPointId: string;
  amazonPackageId?: string | null;
  trackingNumber: string;
  deliveryStatus: string;
  carrierStatus?: string | null;
  consigneeName?: string | null;
  expectedArrivalAt?: string | null;
  driverDropOffAt?: string | null;
  driverDropOffAckAt?: string | null;
  consigneePickupAt?: string | null;
  consigneePickupAckAt?: string | null;
  parcelAgedAt?: string | null;
  agedParcelScanAt?: string | null;
  agedParcelScanAckAt?: string | null;
  dispossessedAt?: string | null;
  disappearedAt?: string | null;
  supersededAt?: string | null;
  replacedByAmazonPackageId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  userId?: string | null;
  dropPointId?: string | null;
  hubCounterID?: string | null;
  notInInventorySync?: boolean | null;
  hiddenInApp?: boolean | null;
  pickupCodeEnteredManually?: boolean | null;
  trackingCodeEnteredManually?: boolean | null;
};

export type AmazonParcelList = {
  parcels: AmazonParcel[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type InternalAmazonParcelStatistics = {
  totalParcels: number;
  totalHubCounters: number;
  activeHubCounters: number;
  averageParcelsPerHubCounter: number;
  dispossessionsThisMonth: number;
  dispossessionsLastMonth: number;
  byStatus: {
    status: string;
    count: number;
    percentage: number;
  }[];
  byHubCounter: {
    amazonAccessPointId: string;
    count: number;
  }[];
  historicalData: {
    period: string;
    count: number;
    date: string;
  }[];
};

export type InternalAllAmazonParcelsQuery = {
  internalAllAmazonParcels: AmazonParcelList;
};

export type InternalAmazonParcelStatisticsQuery = {
  internalAmazonParcelStatistics: InternalAmazonParcelStatistics;
};

export type AmazonParcelSortField =
  | 'trackingNumber'
  | 'createdAt'
  | 'updatedAt'
  | 'driverDropOffAt'
  | 'driverDropOffAckAt'
  | 'consigneePickupAt'
  | 'consigneePickupAckAt'
  | 'parcelAgedAt'
  | 'agedParcelScanAt'
  | 'agedParcelScanAckAt'
  | 'consigneeName'
  | 'amazonAccessPointId'
  | 'hubCounterID'
  | 'deliveryStatus'
  | 'expectedArrivalAt'
  | 'dispossessedAt';

export const AMAZON_PARCEL_STATUSES = [
  'INBOUND',
  'READY_FOR_CONSIGNEE_PICKUP',
  'READY_FOR_UPS_PICKUP',
  'AWAITS_DRIVER_PICKUP',
  'DISPOSSESSIONS',
  'UNKNOWN',
];

export const GET_AMAZON_PARCELS = gql`
  query InternalAllAmazonParcels(
    $amazonAccessPointIds: [String!]
    $statuses: [UPSQueryType!]
    $dateFilters: AmazonDateFiltersInput
    $trackingNumberSearch: String
    $sortBy: AmazonParcelSortField
    $sortOrder: SortOrder
    $limit: Int
    $offset: Int
    $includeSuperseded: Boolean
  ) {
    internalAllAmazonParcels(
      amazonAccessPointIds: $amazonAccessPointIds
      statuses: $statuses
      dateFilters: $dateFilters
      trackingNumberSearch: $trackingNumberSearch
      sortBy: $sortBy
      sortOrder: $sortOrder
      limit: $limit
      offset: $offset
      includeSuperseded: $includeSuperseded
    ) {
      parcels {
        amazonAccessPointId
        amazonPackageId
        trackingNumber
        deliveryStatus
        carrierStatus
        consigneeName
        expectedArrivalAt
        driverDropOffAt
        driverDropOffAckAt
        consigneePickupAt
        consigneePickupAckAt
        parcelAgedAt
        agedParcelScanAt
        agedParcelScanAckAt
        dispossessedAt
        disappearedAt
        supersededAt
        replacedByAmazonPackageId
        createdAt
        updatedAt
        userId
        dropPointId
        hubCounterID
        notInInventorySync
        hiddenInApp
        pickupCodeEnteredManually
        trackingCodeEnteredManually
      }
      totalCount
      limit
      offset
      hasMore
    }
  }
`;

export const GET_AMAZON_PARCEL_STATISTICS = gql`
  query InternalAmazonParcelStatistics($amazonAccessPointIds: [String!]) {
    internalAmazonParcelStatistics(amazonAccessPointIds: $amazonAccessPointIds) {
      totalParcels
      totalHubCounters
      activeHubCounters
      averageParcelsPerHubCounter
      dispossessionsThisMonth
      dispossessionsLastMonth
      byStatus {
        status
        count
        percentage
      }
      byHubCounter {
        amazonAccessPointId
        count
      }
      historicalData {
        period
        count
        date
      }
    }
  }
`;

export const INTERNAL_AMAZON_CONSIGNEE_SCAN = gql`
  mutation InternalAmazonConsigneePickupScan(
    $amazonAccessPointId: String!
    $trackingNumber: String!
    $comment: String!
    $internalUserId: String!
  ) {
    internalAmazonConsigneePickupScan(
      amazonAccessPointId: $amazonAccessPointId
      trackingNumber: $trackingNumber
      comment: $comment
      internalUserId: $internalUserId
    )
  }
`;

export const INTERNAL_AMAZON_AGED_PARCEL_SCAN = gql`
  mutation InternalAmazonAgedParcelScan(
    $amazonAccessPointId: String!
    $trackingNumber: String!
    $comment: String!
    $internalUserId: String!
  ) {
    internalAmazonAgedParcelScan(
      amazonAccessPointId: $amazonAccessPointId
      trackingNumber: $trackingNumber
      comment: $comment
      internalUserId: $internalUserId
    )
  }
`;

export const SET_AMAZON_PARCEL_HIDDEN_IN_APP = gql`
  mutation InternalSetAmazonParcelHiddenInApp(
    $amazonAccessPointId: String!
    $trackingNumber: String!
    $hidden: Boolean!
    $internalUserId: String!
  ) {
    internalSetAmazonParcelHiddenInApp(
      amazonAccessPointId: $amazonAccessPointId
      trackingNumber: $trackingNumber
      hidden: $hidden
      internalUserId: $internalUserId
    )
  }
`;

export const RESTORE_AMAZON_PARCEL_SIBLING = gql`
  mutation InternalRestoreAmazonParcelSibling($amazonAccessPointId: String!, $amazonPackageId: String!, $internalUserId: String!) {
    internalRestoreAmazonParcelSibling(
      amazonAccessPointId: $amazonAccessPointId
      amazonPackageId: $amazonPackageId
      internalUserId: $internalUserId
    )
  }
`;
